<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import IconChevronUp from "@/assets/icons/IconChevronUp.vue";
import { reportYearParamsList } from "@/composables/list/params.list";
import type { Report, MonthlyData, YearData } from "@/types/reports";

// Тип для ключей YearData, которые содержат MonthlyData
type MonthlyDataKeys = {
  [K in keyof YearData]: YearData[K] extends MonthlyData ? K : never;
}[keyof YearData];

interface TableRow {
  parameter: string;
  paramKey: string;
  [key: `month_${number}`]: number | string | null;
}

// Месяца
type MonthColumn = `month_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`;

// Все возможные ключи колонок
type ColumnKey = "parameter" | "paramKey" | MonthColumn;

// Интерфейс колонки
interface Column {
  key: ColumnKey;
  label: string;
}

const props = defineProps<{
  report: Report;
}>();

// Текущий отчет
const currentMonthReport = computed(() => props.report);

// Названия месяцев
const monthNames = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

// Виртуальные параметры (для группировки, без данных)
const VIRTUAL_PARAMS: Record<string, string> = {
  prosecutor_decisions_group: "Принято решений прокурором (из стр. 5)",
  prosecutor_decisions_court_group: "Направлено в суд",
  suspended_group: "Приостановленно"
};

// Конфигурация вложенных параметров (поддерживает 2 уровня вложенности)
const NESTED_PARAMS: Record<string, string[]> = {
  prosecutor_decisions_group: [
    "prosecutor_decisions_court_group",
    "prosecutor_decisions_terminated",
    "returned_for_investigation"
  ],
  prosecutor_decisions_court_group: [
    "prosecutor_decisions_to_court",
    "prosecutor_decisions_medical_measures"
  ],
  suspended_group: [
    "suspended_unidentified_person",
    "suspended_location_unknown",
    "suspended_no_possibility"
  ]
};

// Порядок отображения параметров
const PARAMS_ORDER = [
  "accepted_to_production",
  "completed_cases",
  "sent_to_prosecutor",
  "prosecutor_decisions_group", // виртуальный параметр
  "court_petition_427",
  "court_petition_446",
  "terminated_cases",
  "suspended_group", // виртуальный параметр
  "investigated_overdue",
  "terminated_rehabilitating",
  "terminated_rehabilitating_detained",
  "acquitted_and_terminated",
  "acquitted_and_terminated_detained"
];

// Все параметры (реальные + виртуальные) - используется для поиска в getNestedParams
const allParameters = [
  ...Object.entries(reportYearParamsList).map(([key, label]) => ({ key, label })),
  ...Object.entries(VIRTUAL_PARAMS).map(([key, label]) => ({ key, label }))
];

// Конфигурация параметров 1 уровня
const parameters = PARAMS_ORDER.map(key => {
  const param = allParameters.find(p => p.key === key);
  return param || null;
}).filter((item): item is { key: string; label: string } => item !== null);

// Преобразование номера месяца (1-12) в ключ MonthlyData
const getMonthKey = (monthNumber: number): keyof MonthlyData => {
  const monthKeys: (keyof MonthlyData)[] = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december"
  ];
  return monthKeys[monthNumber - 1];
};

// Проверка, является ли параметр виртуальным
const isVirtualParam = (paramKey: string): boolean => {
  return paramKey in VIRTUAL_PARAMS;
};

// Конфигурация колонок: первая колонка - параметр, остальные - все 12 месяцев
const columns = computed((): Column[] => {
  const cols: Column[] = [{ key: "parameter", label: "Название параметра" }];

  // Добавляем колонки для всех 12 месяцев
  for (let i = 1; i <= 12; i++) {
    cols.push({ key: `month_${i}` as MonthColumn, label: monthNames[i - 1] });
  }

  return cols;
});

// Трансформированные данные: строки = параметры, колонки = месяцы
const tableData = computed(() => {
  if (!currentMonthReport.value) return [];

  // Собираем все вложенные параметры в один Set для быстрой проверки
  const nestedParamsSet = new Set<string>();
  Object.values(NESTED_PARAMS).forEach((nestedKeys) => {
    nestedKeys.forEach((key) => nestedParamsSet.add(key));
  });

  // Фильтруем параметры, исключая вложенные
  const filteredParams = parameters.filter(param => !nestedParamsSet.has(param.key));

  return filteredParams.map(param => {
    const row: TableRow = {
      parameter: param.label,
      paramKey: param.key
    };

    // Для виртуальных параметров не заполняем данные (только заголовок)
    if (isVirtualParam(param.key)) {
      for (let i = 1; i <= 12; i++) {
        row[`month_${i}`] = "-";
      }
    } else {
      // Для реальных параметров извлекаем данные из MonthlyData
      const monthlyData = (currentMonthReport.value as Report)[param.key as MonthlyDataKeys];

      if (monthlyData) {
        for (let i = 1; i <= 12; i++) {
          const monthKey = getMonthKey(i);
          row[`month_${i}`] = monthlyData[monthKey];
        }
      } else {
        // Если данных нет, заполняем прочерками
        for (let i = 1; i <= 12; i++) {
          row[`month_${i}`] = "-";
        }
      }
    }

    return row;
  });
});

// Реф на контейнер таблицы для горизонтального скролла
const scrollContainer = ref<HTMLElement | null>(null);
// Реф на body таблицы для вертикального скролла
const tableBody = ref<HTMLElement | null>(null);

// Флаг. Наличие скролла у контейнера таблицы
const hasScroll = ref(false);

// Ref для отслеживания раскрытых параметров
const expandedParams = ref<Set<string>>(new Set());

// Объект скроллбара
let ps: PerfectScrollbar | null = null;
let resizeObserverInstance: ResizeObserver | null = null;

// Проверка, раскрыт ли параметр
const isParamExpanded = (paramKey: string) => expandedParams.value.has(paramKey);

// Функция раскрытия/закрытия вложенных параметров
const toggleParamExpansion = (paramKey: string) => {
  const newSet = new Set(expandedParams.value);
  if (newSet.has(paramKey)) {
    newSet.delete(paramKey);
  } else {
    newSet.add(paramKey);
  }
  expandedParams.value = newSet;
  updateScroll();
};

// Проверка, есть ли вложенные параметры
const hasNestedParams = (paramKey: string): boolean => {
  return paramKey in NESTED_PARAMS;
};

// Получение вложенных параметров для родительского параметра
const getNestedParams = (parentKey: string) => {
  const nestedKeys = NESTED_PARAMS[parentKey] || [];
  return nestedKeys
    .map((key) => {
      // Ищем в allParameters, чтобы найти вложенные параметры
      const param = allParameters.find(p => p.key === key);
      if (param) {
        return { key, label: param.label };
      }
      return null;
    })
    .filter((item): item is { key: string; label: string } => item !== null);
};

// Обновление скролла
const updateScroll = () => {
  setTimeout(() => {
    checkScroll();
    ps?.update();
  }, 0);
};

// Получение значения вложенного параметра для конкретного месяца
const getNestedParamValue = (paramKey: string, monthNumber: number) => {
  if (!currentMonthReport.value) return "-";

  // Для виртуальных параметров возвращаем прочерк
  if (isVirtualParam(paramKey)) {
    return "-";
  }

  // Получаем MonthlyData объект по ключу параметра
  const monthlyData = (currentMonthReport.value as Report)[paramKey as MonthlyDataKeys];

  if (!monthlyData) return "-";

  // Преобразуем номер месяца в ключ MonthlyData
  const monthKey = getMonthKey(monthNumber);
  const value = monthlyData[monthKey];
  
  if (value !== null) {
    return value.toLocaleString("ru-RU");
  } else {
    return "-";
  }
};

// Форматирование значения ячейки в зависимости от типа
const formatCellValue = (row: TableRow, columnKey:  keyof TableRow | `month_${number}`) => {
  const value = row[columnKey];

  // Для колонки "parameter" возвращаем название как есть
  if (columnKey === "parameter") {
    return value ?? "-";
  }

  if (value !== null) {
    return value.toLocaleString("ru-RU");
  } else {
    return "-";
  }
};

// Проверка наличия скролла у контейнера таблицы.
const checkScroll = () => {
  if (tableBody.value) {
    hasScroll.value = tableBody.value.scrollHeight > tableBody.value.clientHeight;
  }
};

// Автоматически раскрываем все параметры с вложенными элементами при первой загрузке
watch(tableData, (newData) => {
  if (newData.length > 0 && expandedParams.value.size === 0) {
    const paramsToExpand = new Set<string>();

    // Раскрываем параметры первого уровня
    newData.forEach(row => {
      if (hasNestedParams(row.paramKey)) {
        paramsToExpand.add(row.paramKey);

        // Раскрываем параметры второго уровня
        const nestedParams = getNestedParams(row.paramKey);
        nestedParams.forEach(nestedParam => {
          if (hasNestedParams(nestedParam.key)) {
            paramsToExpand.add(nestedParam.key);
          }
        });
      }
    });

    expandedParams.value = paramsToExpand;
  }
}, { immediate: true });

onMounted(() => {
  // Инициализация скролла на tableBody (оба направления)
  if (tableBody.value) {
    ps = new PerfectScrollbar(tableBody.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });

    checkScroll();

    // Синхронизация горизонтального скролла с header (кроме зафиксированных колонок)
    tableBody.value.addEventListener("ps-scroll-x", () => {
      const tableBodyEl = tableBody.value;
      const scrollContainerEl = scrollContainer.value;

      if (scrollContainerEl && tableBodyEl) {
        const header = scrollContainerEl.querySelector(".main-table__header") as HTMLElement;
        if (header) {
          // Применяем transform только к незафиксированным колонкам
          const headerCols = header.querySelectorAll(".main-table__col--header:not(.main-table__col--sticky)");
          headerCols.forEach((col) => {
            (col as HTMLElement).style.transform = `translateX(-${tableBodyEl.scrollLeft}px)`;
          });
        }
      }
    });

    // Обсервер на изменения размера контейнера для обновления позиции скролла
    resizeObserverInstance = new ResizeObserver(() => {
      checkScroll();
      ps?.update();
    });
    resizeObserverInstance.observe(tableBody.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserverInstance) {
    resizeObserverInstance.disconnect();
    resizeObserverInstance = null;
  }
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

</script>

<template>
  <div class="main-table__wrapper">
    <div class="main-table">
      <div
        ref="scrollContainer"
        class="main-table__scroll-container"
      >
        <!--   Шапка таблицы   -->
        <div
          :class="{ 'main-table__header--with-scroll': hasScroll }"
          class="main-table__header main-table__row main-table__row--header"
          :style="{ gridTemplateColumns: 'minmax(700px, 2fr) repeat(12, minmax(120px, 1fr))' }"
        >
          <div
            v-for="(column, idx) in columns"
            :key="column.key"
            class="main-table__col main-table__col--header"
            :class="{ 'main-table__col--sticky': idx === 0 }"
          >
            {{ column.label }}
          </div>
        </div>
        <!--   Основное содержимое таблицы    -->
        <div
          ref="tableBody"
          class="main-table__body"
        >
          <template
            v-for="(row, index) in tableData"
            :key="index"
          >
            <!-- Основная строка параметра -->
            <div
              class="main-table__row"
              :style="{ gridTemplateColumns: 'minmax(700px, 2fr) repeat(12, minmax(120px, 1fr))' }"
            >
              <div
                v-for="(column, idx) in columns"
                :key="column.key"
                class="main-table__col"
                :class="{
                  'main-table__col--sticky': idx === 0,
                  'main-table__col--expandable': idx === 0 && hasNestedParams(row.paramKey)
                }"
              >
                <button
                  v-if="idx === 0 && hasNestedParams(row.paramKey)"
                  class="expand-button expand-button--nested"
                  :class="{ 'expand-button--expanded': isParamExpanded(row.paramKey) }"
                  @click="toggleParamExpansion(row.paramKey)"
                >
                  <IconChevronUp />
                </button>
                <div class="main-table__message">
                  {{ formatCellValue(row, column.key) }}
                </div>
              </div>
            </div>

            <!-- Вложенные параметры (1 уровень) -->
            <template v-if="hasNestedParams(row.paramKey) && isParamExpanded(row.paramKey)">
              <template
                v-for="nestedParam in getNestedParams(row.paramKey)"
                :key="nestedParam.key"
              >
                <!-- Строка вложенного параметра 1 уровня -->
                <div
                  class="main-table__row main-table__row--nested"
                  :class="{ 'main-table__row--with-scroll': hasScroll }"
                  :style="{ gridTemplateColumns: '32px minmax(668px, 2fr) repeat(12, minmax(120px, 1fr))' }"
                >
                  <!-- Пустая колонка для отступа -->
                  <div class="main-table__col main-table__col--spacer main-table__col--sticky" />
                  <!-- Колонка с вложенным параметром -->
                  <div class="main-table__col main-table__col--nested main-table__col--sticky">
                    <button
                      v-if="hasNestedParams(nestedParam.key)"
                      class="expand-button expand-button--nested"
                      :class="{ 'expand-button--expanded': isParamExpanded(nestedParam.key) }"
                      @click="toggleParamExpansion(nestedParam.key)"
                    >
                      <IconChevronUp />
                    </button>
                    <div class="main-table__message">
                      {{ nestedParam.label }}
                    </div>
                  </div>
                  <!-- Колонки с данными по месяцам -->
                  <div
                    v-for="i in 12"
                    :key="`month_${i}`"
                    class="main-table__col"
                  >
                    <div class="main-table__message">
                      {{ getNestedParamValue(nestedParam.key, i) }}
                    </div>
                  </div>
                </div>

                <!-- Вложенные параметры (2 уровень) -->
                <template v-if="hasNestedParams(nestedParam.key) && isParamExpanded(nestedParam.key)">
                  <div
                    v-for="nestedParam2 in getNestedParams(nestedParam.key)"
                    :key="nestedParam2.key"
                    class="main-table__row main-table__row--nested main-table__row--nested-2"
                    :class="{ 'main-table__row--with-scroll': hasScroll }"
                    :style="{ gridTemplateColumns: '64px minmax(636px, 2fr) repeat(12, minmax(120px, 1fr))' }"
                  >
                    <!-- Пустая колонка для отступа (двойной) -->
                    <div class="main-table__col main-table__col--spacer main-table__col--sticky" />
                    <!-- Колонка с вложенным параметром 2 уровня -->
                    <div class="main-table__col main-table__col--nested main-table__col--nested-2 main-table__col--sticky">
                      <div class="main-table__message">
                        {{ nestedParam2.label }}
                      </div>
                    </div>
                    <!-- Колонки с данными по месяцам -->
                    <div
                      v-for="i in 12"
                      :key="`month_${i}`"
                      class="main-table__col"
                    >
                      <div class="main-table__message">
                        {{ getNestedParamValue(nestedParam2.key, i) }}
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/style/perfect-scrollbar";

.main-table__wrapper {
  background-color: var(--background-01);
  padding: 10px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  min-height: 0;
}

.main-table {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;

  &__scroll-container {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    min-height: 0;
    min-width: 0;
    width: 100%;
  }

  &__status {
    min-width: 24px;
    min-height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__footer {
    display: flex;
    justify-content: center;
    margin-top: 10px;
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    flex-shrink: 0;
    min-width: 2140px;

    &--with-scroll {
      margin-right: 28px;
    }
  }

  &__body {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background-color: var(--background-bg-white);
  }

  &__footer {
    flex-shrink: 0;
  }

  &__row {
    display: grid;
    flex-shrink: 0;
    min-width: 2140px;

    &:not(&--header):not(:last-child) {
      border-bottom: 1px solid var(--purple-purple20);
    }

    &--header {
      position: sticky;
      top: 0;
      z-index: 1;
    }

    &--nested {
      grid-template-columns: 32px minmax(668px, 2fr) repeat(12, minmax(120px, 1fr));
    }

    &--nested-2 {
      grid-template-columns: 64px minmax(636px, 2fr) repeat(12, minmax(120px, 1fr));
    }
  }

  &__message {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    min-width: 0;
  }

  &__col {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background-color: var(--background-bg-white);
    min-width: 0;

    //&:not(&--header) {
    //  border-right: 1px solid var(--purple-purple20);
    //
    //  // При отсутствии скролла последний элемент без border
    //  .main-table__row:not(.main-table__row--with-scroll) &:last-child {
    //    border-right: 0;
    //  }
    //}

    // Центрирование данных в колонках месяцев (все колонки кроме первой и вложенных названий)
    &:not(&--header):not(&--sticky):not(&--spacer):not(&--nested):not(&--nested-2) {
      justify-content: center;
    }

    &--header {
      font-weight: 500;
      font-size: 14px;
      line-height: 22px;
      color: var(--text-text-main);
      background-color: var(--background-01);

      &:not(:first-child) {
        justify-content: center;
      }
    }

    &--sticky {
      position: sticky;
      left: 0;
      z-index: 2;
      background-color: var(--background-bg-white);

      // Для header нужен более высокий z-index
      &.main-table__col--header {
        z-index: 3;
        background-color: var(--background-01);
      }

      // Для вложенной колонки сдвигаем на 32px (ширина spacer)
      &.main-table__col--nested {
        left: 32px;
      }

      // Для вложенной колонки 2 уровня сдвигаем на 64px (двойная ширина spacer)
      &.main-table__col--nested-2 {
        left: 64px;
      }
    }

    &--spacer {
      padding: 0;
    }

    &--nested {
      padding-left: 10px;
      border-left: 1px solid var(--purple-purple20);
    }

    &--nested-2 {
      padding-left: 10px;
      border-left: 1px solid var(--purple-purple20);
    }

    &--expandable {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .expand-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    flex-shrink: 0;
    transform: rotate(180deg);

    &--nested {
      width: 16px;
      height: 16px;
    }

    &--expanded {
      transform: rotate(0);
    }

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>